import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { Workspace } from "@sable/api-contract";
import { Wordmark } from "../components/Shell.tsx";
import { Field } from "../components/ui.tsx";
import { t } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { keys } from "../lib/queries.ts";
import { COUNTRIES, guessCountry, timeZones } from "../lib/regions.ts";

export function Onboarding() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const initial = guessCountry();
  const [name, setName] = useState("");
  const [country, setCountry] = useState(initial.code);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [units, setUnits] = useState(initial.units);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = COUNTRIES.find((c) => c.code === country)!;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const ws = await request<Workspace>("/workspaces", {
        method: "POST",
        body: { name: name.trim(), country, currency: selected.currency, timezone, units, locale: navigator.language || "en-US" },
      });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(`/w/${ws.id}/inventory`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="center-page">
      <form className="onboard stack-lg" onSubmit={submit}>
        <Wordmark />
        <div>
          <h1>{t("onboarding.title")}</h1>
          <p className="muted">{t("onboarding.body")}</p>
        </div>
        <div className="card stack">
          <Field label={t("onboarding.name")}>
            <input className="input" required maxLength={80} value={name} placeholder={t("onboarding.namePlaceholder")} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid-2">
            <Field label={t("onboarding.country")}>
              <select className="select" value={country} onChange={(e) => {
                setCountry(e.target.value);
                setUnits(COUNTRIES.find((c) => c.code === e.target.value)!.units);
              }}>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
            <Field label={t("onboarding.currency")}>
              <input className="input" value={selected.currency} readOnly />
            </Field>
          </div>
          <Field label={t("onboarding.timezone")}>
            <select className="select" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {timeZones().map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label={t("onboarding.units")}>
            <select className="select" value={units} onChange={(e) => setUnits(e.target.value as "imperial" | "metric")}>
              <option value="imperial">{t("onboarding.imperial")}</option>
              <option value="metric">{t("onboarding.metric")}</option>
            </select>
          </Field>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="btn btn-primary" disabled={busy || !name.trim()}>{busy ? t("app.saving") : t("onboarding.create")}</button>
        </div>
      </form>
    </div>
  );
}
